import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import { masterApi } from "../../apis/masterApi";
import { encodeData } from "../../helper/common/encodeData";
import useUserDetails from "../../helper/userData/useUserDetails";
import { BidBulkColumnHeader, BULK_UPDATE_COLUMN } from "./components/BidBulkColumnUpdate";
import BidSlotCasesPanel from "./components/BidSlotCasesPanel";
import BidCampaignSelectPaper from "./components/BidCampaignSelectPaper";
import BidTablePagination, {
  paginateIds,
} from "./components/BidTablePagination";
import { timePeriodToCustomDate } from "../../helper/Quadrant/quadrantHelper";
import {
  buildCubeFiltersFromFilterModule,
  filterModuleHasContent,
} from "./lib/cubeFilters";
import {
  AUTOMATION_MODE_OPTIONS,
  TARGETING_EXCLUSION_TOOLTIP,
  DAY_LABELS,
  CAMPAIGN_FETCH_LIMIT,
  CAMPAIGN_TABLE_PAGE_SIZE,
  CAMPAIGN_SELECT_ALL_ID,
  CAMPAIGN_SELECT_ALL_LABEL,
  GAP_HANDLING_SECTION_TOOLTIP,
  SLOT_STATUS_OPTIONS,
  GAP_OPTIONS,
  HEADING_TITLE_COLOR,
  SLOT_END_HOURS,
  SLOT_START_HOURS,
  RULE_TYPE,
  CASE_TYPES,
} from "./lib/constants";
import {
  createDefaultCampaignBidCaps,
  createDefaultSlot,
  createDefaultCase,
  createInitialFormState,
  apiRuleToForm,
  formToApiRequestBody,
  buildAutomationModeResetPatch,
} from "./lib/payload";
import {
  buildCampaignListApiFilters,
  buildSelectedCampaignsApiFilter,
  formatDayDisplayName,
  formatValidationIssueMessage,
  getCampaignDisplayName,
  getCampaignMongoId,
  getCampaignPlatformId,
  getPortfolioDisplayName,
  getPortfolioPlatformId,
  getCampaignStatusDotColor,
  BUDGET_BULK_MODE,
  computeBudgetFromBase,
  getMaxBudgetError,
  getMinBudgetError,
  getMaxCampaignBid,
  getMinCampaignBudget,
  getCampaignBudgetCurrencyCode,
  isValidCampaignListItem,
  mapCampaignStatusForFilter,
  normalizeMaxBudgetInput,
  normalizeMinBudgetInput,
} from "./lib/helpers";
import { getSlotDurationErrors, validateForm } from "./lib/validation";
import { formatSpBidBound } from "./lib/bidCapsConfig";
import {
  BID_V2_COLORS,
  dayCircleSx,
  durationLabelSx,
  durationFieldsGridSx,
  durationRowActionsSx,
  durationRowLayoutSx,
  slotActionIconSx,
  tableBodyAlign,
  tableHeaderAlign,
  fieldLabelSx,
  infoAlertSx,
  nestedScheduleBoxSx,
  radioLabelSx,
  sectionCardSx,
  sectionHeaderWrapSx,
  sectionStepBadgeSx,
  sectionSubtitleSx,
  sectionTitleSx,
  selectFieldSx,
  slotPaperSx,
  tableBodyCellSx,
  tableHeadCellSx,
  textFieldSx,
  numberFieldInputProps,
} from "./lib/bidAutomationStyles";

const campaignNameCellSx = {
  fontWeight: 600,
  width: 300,
  maxWidth: 300,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const CAMPAIGN_SELECT_ALL_OPTION = {
  id: CAMPAIGN_SELECT_ALL_ID,
  __selectAll: true,
  name: CAMPAIGN_SELECT_ALL_LABEL,
};

function BidInfoTooltip({ title }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlinedIcon
        sx={{ fontSize: 18, color: BID_V2_COLORS.muted, cursor: "help", verticalAlign: "middle" }}
        aria-label="More information"
      />
    </Tooltip>
  );
}

function BidSectionHeader({ step, title, description, tooltip }) {
  return (
    <Box sx={sectionHeaderWrapSx}>
      <Box sx={sectionStepBadgeSx} aria-hidden>
        {step}
      </Box>
      <Box sx={{ minWidth: 0, pt: 0.1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography component="h2" sx={sectionTitleSx}>
            {title}
          </Typography>
          {tooltip ? <BidInfoTooltip title={tooltip} /> : null}
        </Stack>
        {description ? (
          <Typography sx={{ ...sectionSubtitleSx, mt: 0.4 }}>{description}</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function CampaignTableNameCell({ name, campaignId, loading }) {
  if (loading) {
    return <Skeleton variant="text" width="75%" height={22} />;
  }
  const displayName = name || campaignId || "";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Tooltip title={displayName} arrow placement="top" disableHoverListener={!displayName}>
        <Typography
          variant="body2"
          component="span"
          sx={{ ...campaignNameCellSx, display: "block", flex: 1, minWidth: 0 }}
        >
          {displayName}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function BidAutomationCreation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const [userDetails] = useUserDetails();
  const email = userDetails?.user?.email ?? localStorage.getItem("email") ?? "local@user";

  const [form, setForm] = useState(createInitialFormState);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignSearchInput, setCampaignSearchInput] = useState("");
  const [debouncedCampaignSearch, setDebouncedCampaignSearch] = useState("");
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingSelectedCampaigns, setIsLoadingSelectedCampaigns] = useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState({});
  const [isLoadingRule, setIsLoadingRule] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [campaignBudgetPage, setCampaignBudgetPage] = useState(0);
  const [expandedPerfCaseId, setExpandedPerfCaseId] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [clearAllCampaignsConfirmOpen, setClearAllCampaignsConfirmOpen] = useState(false);
  const [automationModeConfirmOpen, setAutomationModeConfirmOpen] = useState(false);
  const [pendingAutomationMode, setPendingAutomationMode] = useState(null);

  const { clientId, selectedPlatform } = useGlobalContext();
  const userPlatform = selectedPlatform ?? "amazon";

  const scheduleStartRef = useRef(null);
  const scheduleEndRef = useRef(null);
  const nameValidateTimerRef = useRef(null);
  const scheduleValidateTimerRef = useRef(null);
  const campaignSearchDebounceRef = useRef(null);
  const campaignListAbortRef = useRef(null);
  const autoExpandedPerfRef = useRef(false);
  const suppressValidationRef = useRef(Boolean(editId));
  const [nameValidation, setNameValidation] = useState({
    loading: false,
    valid: true,
    issues: [],
    message: "",
  });
  const [scheduleValidation, setScheduleValidation] = useState({
    loading: false,
    valid: true,
    issues: [],
  });
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const slotDurationErrors = useMemo(() => getSlotDurationErrors(form), [form]);
  const ruleScheduleDateBounds = useMemo(() => {
    const ruleMin = form.scheduleStartDate || todayISO;
    const ruleMax = form.scheduleEndDate || undefined;
    return { min: ruleMin, max: ruleMax };
  }, [form.scheduleStartDate, form.scheduleEndDate, todayISO]);
  const nonNegativeValue = useCallback((value) => {
    if (value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    return num < 0 ? "0" : value;
  }, []);
  const paginatedCampaignIds = useMemo(
    () => paginateIds(form.selectedCampaignIds, campaignBudgetPage),
    [form.selectedCampaignIds, campaignBudgetPage]
  );

  useEffect(() => {
    setCampaignBudgetPage(0);
  }, [form.selectedCampaignIds.length]);

  const selectedCampaignIdsKey = useMemo(
    () => form.selectedCampaignIds.join(","),
    [form.selectedCampaignIds]
  );

  const selectedPortfolioPlatformIds = useMemo(
    () =>
      selectedPortfolios
        .map(getPortfolioPlatformId)
        .filter(Boolean),
    [selectedPortfolios]
  );

  const isEditHydrating = Boolean(editId) && isLoadingRule;

  const isCampaignNameLoading = useCallback(
    (mid) =>
      form.selectedCampaignIds.includes(mid) &&
      isLoadingSelectedCampaigns &&
      !form.campaignMeta[mid]?.name,
    [form.selectedCampaignIds, form.campaignMeta, isLoadingSelectedCampaigns]
  );

  const openDatePickerFromField = useCallback((e) => {
    const input = e.currentTarget.querySelector('input[type="date"]');
    input?.showPicker?.();
  }, []);

  const enableValidation = useCallback(() => {
    suppressValidationRef.current = false;
  }, []);

  const patchForm = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const applyAutomationModeChange = useCallback(
    (nextMode) => {
      enableValidation();
      setForm((prev) => {
        if (prev.automationMode === nextMode) return prev;
        return {
          ...prev,
          ...buildAutomationModeResetPatch(prev, nextMode),
        };
      });
      setExpandedPerfCaseId(null);
      setCampaignBudgetPage(0);
    },
    [enableValidation]
  );

  const handleAutomationModeSelect = useCallback(
    (nextMode) => {
      if (form.automationMode === nextMode) return;
      setPendingAutomationMode(nextMode);
      setAutomationModeConfirmOpen(true);
    },
    [form.automationMode]
  );

  const handleCancelAutomationModeSwitch = useCallback(() => {
    setPendingAutomationMode(null);
    setAutomationModeConfirmOpen(false);
  }, []);

  const handleConfirmAutomationModeSwitch = useCallback(() => {
    if (pendingAutomationMode) {
      applyAutomationModeChange(pendingAutomationMode);
    }
    setPendingAutomationMode(null);
    setAutomationModeConfirmOpen(false);
  }, [applyAutomationModeChange, pendingAutomationMode]);

  const pendingAutomationModeLabel = useMemo(
    () =>
      AUTOMATION_MODE_OPTIONS.find((o) => o.value === pendingAutomationMode)?.label ??
      "this mode",
    [pendingAutomationMode]
  );

  useEffect(() => {
    suppressValidationRef.current = Boolean(editId);
  }, [editId]);

  useEffect(() => {
    if (form.activeCampaignFilter === "Archived") {
      patchForm({ activeCampaignFilter: "All" });
    }
  }, [form.activeCampaignFilter, patchForm]);

  const campaignAutocompleteOptions = useMemo(() => {
    if (!campaignOptions.length) return campaignOptions;
    return [CAMPAIGN_SELECT_ALL_OPTION, ...campaignOptions];
  }, [campaignOptions]);

  const allVisibleCampaignsSelected = useMemo(
    () =>
      campaignOptions.length > 0 &&
      campaignOptions.every((c) =>
        form.selectedCampaignIds.includes(getCampaignMongoId(c))
      ),
    [campaignOptions, form.selectedCampaignIds]
  );

  const someVisibleCampaignsSelected = useMemo(
    () =>
      campaignOptions.some((c) =>
        form.selectedCampaignIds.includes(getCampaignMongoId(c))
      ),
    [campaignOptions, form.selectedCampaignIds]
  );

  const selectedCampaignObjects = useMemo(() => {
    const dropdownMap = new Map(campaignOptions.map((c) => [getCampaignMongoId(c), c]));
    return form.selectedCampaignIds.map((id) => {
      const fromSelected = selectedCampaignDetails[id];
      const fromDropdown = dropdownMap.get(id);
      const meta = form.campaignMeta[id] ?? {};
      const resolved = fromSelected ?? fromDropdown;
      if (resolved) return resolved;
      return {
        id,
        objectId: id,
        name: meta.name || id,
        platform_id: meta.platformId,
        daily_budget: 0,
      };
    });
  }, [campaignOptions, selectedCampaignDetails, form.selectedCampaignIds, form.campaignMeta]);

  useEffect(() => {
    if (campaignSearchDebounceRef.current) {
      clearTimeout(campaignSearchDebounceRef.current);
    }
    campaignSearchDebounceRef.current = setTimeout(() => {
      setDebouncedCampaignSearch(campaignSearchInput.trim());
    }, 400);
    return () => {
      if (campaignSearchDebounceRef.current) {
        clearTimeout(campaignSearchDebounceRef.current);
      }
    };
  }, [campaignSearchInput]);

  useEffect(() => {
    let cancelled = false;
    const loadPortfolios = async () => {
      if (!clientId) {
        setPortfolioOptions([]);
        setIsLoadingPortfolios(false);
        return;
      }
      setIsLoadingPortfolios(true);
      try {
        const result = await masterApi(
          {
            source: userPlatform,
            fields: "name,platform_id,id",
            limit: 500,
          },
          "getPortfolios",
          false
        );
        if (!cancelled) {
          setPortfolioOptions(result?.data?.data ?? []);
        }
      } catch {
        if (!cancelled) toast.error("Failed to fetch portfolios");
      } finally {
        if (!cancelled) setIsLoadingPortfolios(false);
      }
    };
    loadPortfolios();
    return () => {
      cancelled = true;
    };
  }, [clientId, userPlatform]);

  useEffect(() => {
    let cancelled = false;
    const loadCampaigns = async () => {
      if (campaignListAbortRef.current) {
        campaignListAbortRef.current.abort();
      }
      const controller = new AbortController();
      campaignListAbortRef.current = controller;
      setIsLoadingCampaigns(true);
      setCampaignOptions([]);
      try {
        const apiFilters = buildCampaignListApiFilters(
          debouncedCampaignSearch,
          form.activeCampaignFilter,
          selectedPortfolioPlatformIds
        );
        const filteringValue = apiFilters.length ? encodeData(apiFilters) : "";
        const query = [
          `source=${userPlatform}`,
          `limit=${CAMPAIGN_FETCH_LIMIT}`,
          "order_by=daily_budget",
          "sorting_order=DESC",
          filteringValue ? `filtering=${filteringValue}` : "",
        ]
          .filter(Boolean)
          .join("&");
        const result = await axios.get(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/campaigns?${query}`,
          {
            signal: controller.signal,
          }
        );
        if (cancelled) return;
        const payload = result?.data;
        const campaigns =
          (Array.isArray(payload) && payload) ||
          (Array.isArray(payload?.data) && payload.data) ||
          (Array.isArray(payload?.data?.data) && payload.data.data) ||
          [];
        const list = campaigns.filter(isValidCampaignListItem);
        setCampaignOptions(list);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
        if (!cancelled) toast.error("Failed to fetch campaigns");
      } finally {
        if (!cancelled && campaignListAbortRef.current === controller) {
          setIsLoadingCampaigns(false);
          campaignListAbortRef.current = null;
        }
      }
    };
    if (!clientId) {
      setCampaignOptions([]);
      setIsLoadingCampaigns(false);
      return () => {};
    }
    loadCampaigns();
    return () => {
      cancelled = true;
      if (campaignListAbortRef.current) {
        campaignListAbortRef.current.abort();
        campaignListAbortRef.current = null;
      }
    };
  }, [
    clientId,
    debouncedCampaignSearch,
    form.activeCampaignFilter,
    selectedPortfolioPlatformIds,
    userPlatform,
  ]);

  useEffect(() => {
    const ids = selectedCampaignIdsKey ? selectedCampaignIdsKey.split(",") : [];
    if (!ids.length) {
      setSelectedCampaignDetails({});
      setIsLoadingSelectedCampaigns(false);
      return undefined;
    }

    let cancelled = false;
    const loadSelectedCampaigns = async () => {
      setIsLoadingSelectedCampaigns(true);
      try {
        const uniqueIds = [...new Set(ids.map(String).filter(Boolean))];
        const chunks = [];
        for (let i = 0; i < uniqueIds.length; i += CAMPAIGN_FETCH_LIMIT) {
          chunks.push(uniqueIds.slice(i, i + CAMPAIGN_FETCH_LIMIT));
        }

        const responses = await Promise.all(
          chunks.map((chunk) =>
            masterApi(
              {
                source: userPlatform,
                limit: chunk.length,
                order_by: "daily_budget",
                sorting_order: "DESC",
                filtering: encodeData(buildSelectedCampaignsApiFilter(chunk)),
              },
              "campaignList",
              false
            )
          )
        );

        if (cancelled) return;

        const list = responses
          .flatMap((result) => result?.data?.data ?? [])
          .filter(isValidCampaignListItem);

        const detailsById = {};
        list.forEach((campaign) => {
          detailsById[getCampaignMongoId(campaign)] = campaign;
        });
        setSelectedCampaignDetails(detailsById);

        setForm((prev) => {
          const campaignMeta = { ...prev.campaignMeta };
          uniqueIds.forEach((mid) => {
            const c = detailsById[mid];
            if (c) {
              campaignMeta[mid] = {
                ...(campaignMeta[mid] ?? {}),
                name: getCampaignDisplayName(c),
                platformId: getCampaignPlatformId(c),
                status: campaignMeta[mid]?.status ?? mapCampaignStatusForFilter(c),
              };
            } else {
              campaignMeta[mid] = {
                ...(campaignMeta[mid] ?? {}),
                name: campaignMeta[mid]?.name || mid,
              };
            }
          });
          return { ...prev, campaignMeta };
        });
      } catch {
        if (!cancelled) {
          toast.error("Failed to fetch selected campaign details");
          setForm((prev) => {
            const campaignMeta = { ...prev.campaignMeta };
            const uniqueIds = [...new Set(ids.map(String).filter(Boolean))];
            uniqueIds.forEach((mid) => {
              if (!campaignMeta[mid]?.name) {
                campaignMeta[mid] = { ...(campaignMeta[mid] ?? {}), name: mid };
              }
            });
            return { ...prev, campaignMeta };
          });
        }
      } finally {
        if (!cancelled) setIsLoadingSelectedCampaigns(false);
      }
    };

    loadSelectedCampaigns();
    return () => {
      cancelled = true;
    };
  }, [selectedCampaignIdsKey, userPlatform]);

  useEffect(() => {
    if (!editId || !clientId) return;
    let cancelled = false;
    const loadRule = async () => {
      setIsLoadingRule(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set/${editId}?source=${userPlatform}&type=${RULE_TYPE}`
        );
        const rule = data?.data ?? data ?? null;
        if (cancelled) return;
        if (!rule) {
          toast.warning("Rule not found — starting with an empty form");
          setForm(createInitialFormState());
          navigate("/bid-automation-list", { replace: true });
          return;
        }
        setCreatedAt(rule.created_at);
        setForm(apiRuleToForm(rule));
      } catch (error) {
        if (!cancelled) {
          toast.warning(
            error?.response?.data?.message ??
              error?.response?.data?.error ??
              error?.message ??
              "Could not load rule for edit — starting with an empty form"
          );
          setForm(createInitialFormState());
        }
      } finally {
        if (!cancelled) setIsLoadingRule(false);
      }
    };
    loadRule();
    return () => {
      cancelled = true;
    };
  }, [editId, clientId, navigate, userPlatform]);

  useEffect(() => {
    if (!editId || autoExpandedPerfRef.current) return;
    for (const slot of form.slots) {
      for (const caseType of Object.values(CASE_TYPES)) {
        const found = (slot.cases?.[caseType] ?? []).find(
          (c) =>
            filterModuleHasContent(c.perfFilter?.filterModuleFilters)
        );
        if (found) {
          setExpandedPerfCaseId(found.localId);
          autoExpandedPerfRef.current = true;
          return;
        }
      }
    }
  }, [editId, form.slots]);

  useEffect(() => {
    if (!clientId || !form.ruleName?.trim()) {
      setNameValidation({ loading: false, valid: true, issues: [], message: "" });
      return undefined;
    }

    if (suppressValidationRef.current) {
      return undefined;
    }

    if (nameValidateTimerRef.current) {
      clearTimeout(nameValidateTimerRef.current);
    }

    nameValidateTimerRef.current = setTimeout(async () => {
      setNameValidation((prev) => ({ ...prev, loading: true }));
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set:validate-rule?source=${userPlatform}&type=${RULE_TYPE}`,
          {
            issue_types: ["DUPLICATE_RULE_NAME"],
            name: form.ruleName.trim(),
            ...(editId ? { exclude_rule_set_id: editId } : {}),
          }
        );

        const result = data?.data ?? data ?? {};
        const issues = Array.isArray(result.issues) ? result.issues : [];
        const nameIssues = issues.filter((i) => i?.issue_type === "DUPLICATE_RULE_NAME");
        const firstMessage = nameIssues[0]?.message ?? "";

        setNameValidation({
          loading: false,
          valid: nameIssues.length === 0,
          issues: nameIssues,
          message: nameIssues.length ? formatValidationIssueMessage(firstMessage) : "",
        });
      } catch {
        setNameValidation({ loading: false, valid: true, issues: [], message: "" });
      }
    }, 500);

    return () => {
      if (nameValidateTimerRef.current) {
        clearTimeout(nameValidateTimerRef.current);
      }
    };
  }, [clientId, userPlatform, form.ruleName, editId]);

  useEffect(() => {
    if (
      !clientId ||
      !form.scheduleStartDate ||
      !form.scheduleEndDate ||
      !Array.isArray(form.selectedCampaignIds) ||
      form.selectedCampaignIds.length === 0
    ) {
      setScheduleValidation({ loading: false, valid: true, issues: [] });
      return undefined;
    }

    if (suppressValidationRef.current) {
      return undefined;
    }

    if (scheduleValidateTimerRef.current) {
      clearTimeout(scheduleValidateTimerRef.current);
    }

    scheduleValidateTimerRef.current = setTimeout(async () => {
      setScheduleValidation((prev) => ({ ...prev, loading: true }));
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set:validate-rule?source=${userPlatform}&type=${RULE_TYPE}`,
          {
            issue_types: ["CAMPAIGN_SCHEDULE_CONFLICT"],
            campaign_ids: form.selectedCampaignIds,
            schedule_config: {
              start_date: form.scheduleStartDate,
              end_date: form.scheduleEndDate,
            },
            ...(editId ? { exclude_rule_set_id: editId } : {}),
          }
        );

        const result = data?.data ?? data ?? {};
        const issues = Array.isArray(result.issues) ? result.issues : [];
        const scheduleIssues = issues.filter(
          (i) => i?.issue_type === "CAMPAIGN_SCHEDULE_CONFLICT"
        );

        setScheduleValidation({
          loading: false,
          valid: scheduleIssues.length === 0,
          issues: scheduleIssues,
        });
      } catch {
        setScheduleValidation({ loading: false, valid: true, issues: [] });
      }
    }, 500);

    return () => {
      if (scheduleValidateTimerRef.current) {
        clearTimeout(scheduleValidateTimerRef.current);
      }
    };
  }, [
    clientId,
    userPlatform,
    form.scheduleStartDate,
    form.scheduleEndDate,
    form.selectedCampaignIds,
    editId,
  ]);

  const toggleCampaign = (mongoId, campaign) => {
    enableValidation();
    setForm((prev) => {
      const selected = [...prev.selectedCampaignIds];
      const table = { ...prev.campaignTableBids };
      const meta = { ...prev.campaignMeta };
      const idx = selected.indexOf(mongoId);
      if (idx >= 0) {
        selected.splice(idx, 1);
        delete table[mongoId];
        delete meta[mongoId];
      } else {
        selected.push(mongoId);
        table[mongoId] = createDefaultCampaignBidCaps();
        meta[mongoId] = {
          name: getCampaignDisplayName(campaign),
          platformId: getCampaignPlatformId(campaign),
          status: mapCampaignStatusForFilter(campaign),
        };
      }
      return {
        ...prev,
        selectedCampaignIds: selected,
        campaignTableBids: table,
        campaignMeta: meta,
      };
    });
  };

  const applyCampaignSelection = useCallback(
    (campaignList) => {
      enableValidation();
      startTransition(() => {
        setForm((prev) => {
          const selected = [];
          const table = {};
          const meta = {};

          campaignList.forEach((c) => {
            const mid = getCampaignMongoId(c);
            selected.push(mid);
            const existing = prev.campaignTableBids[mid] ?? createDefaultCampaignBidCaps();
            table[mid] = existing;
            meta[mid] = {
              name: getCampaignDisplayName(c),
              platformId: getCampaignPlatformId(c),
              status: mapCampaignStatusForFilter(c),
            };
          });

          return {
            ...prev,
            selectedCampaignIds: selected,
            campaignTableBids: table,
            campaignMeta: meta,
          };
        });
      });
    },
    [enableValidation]
  );

  const handleConfirmClearAllCampaigns = () => {
    applyCampaignSelection([]);
    setClearAllCampaignsConfirmOpen(false);
  };

  const handleCampaignAutocompleteChange = (_, newValue, reason, details) => {
    if (reason === "clear" && form.selectedCampaignIds.length > 0) {
      setClearAllCampaignsConfirmOpen(true);
      return;
    }

    if (details?.option?.__selectAll) {
      const visibleIds = new Set(campaignOptions.map((c) => getCampaignMongoId(c)));
      if (allVisibleCampaignsSelected) {
        const keep = selectedCampaignObjects.filter(
          (c) => !visibleIds.has(getCampaignMongoId(c))
        );
        applyCampaignSelection(keep);
      } else {
        const byId = new Map(
          selectedCampaignObjects.map((c) => [getCampaignMongoId(c), c])
        );
        campaignOptions.forEach((c) => byId.set(getCampaignMongoId(c), c));
        applyCampaignSelection([...byId.values()]);
      }
      return;
    }
    applyCampaignSelection(newValue.filter((c) => !c.__selectAll));
  };

  const updateSlot = (localId, patch) => {
    enableValidation();
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => (s.localId === localId ? { ...s, ...patch } : s)),
    }));
  };

  const applyBulkCampaignMinBid = useCallback((payload) => {
    const mode = payload?.mode || BUDGET_BULK_MODE.SET_ABSOLUTE;
    const value = payload?.value;
    if (mode === BUDGET_BULK_MODE.SET_ABSOLUTE && getMinBudgetError(value)) return;

    setForm((prev) => {
      const nextTableBudgets = { ...prev.campaignTableBids };
      prev.selectedCampaignIds.forEach((mid) => {
        const current = nextTableBudgets[mid] ?? createDefaultCampaignBidCaps();
        const nextMin =
          mode === BUDGET_BULK_MODE.SET_ABSOLUTE
            ? value
            : computeBudgetFromBase(current.min_value, mode, value);
        if (nextMin === null || nextMin === undefined) return;
        nextTableBudgets[mid] = { ...current, min_value: nextMin };
      });
      return { ...prev, campaignTableBids: nextTableBudgets };
    });
  }, []);

  const applyBulkCampaignMaxBid = useCallback((payload) => {
    const mode = payload?.mode || BUDGET_BULK_MODE.SET_ABSOLUTE;
    const value = payload?.value;

    if (mode === BUDGET_BULK_MODE.SET_ABSOLUTE && getMaxBudgetError(value)) {
      return;
    }

    setForm((prev) => {
      const nextTableBudgets = { ...prev.campaignTableBids };
      prev.selectedCampaignIds.forEach((mid) => {
        const current = nextTableBudgets[mid] ?? createDefaultCampaignBidCaps();
        const percentBase = current.max_value;
        const nextMax =
          mode === BUDGET_BULK_MODE.SET_ABSOLUTE
            ? value
            : computeBudgetFromBase(percentBase, mode, value);
        if (nextMax === null || nextMax === undefined) return;
        nextTableBudgets[mid] = { ...current, max_value: nextMax };
      });
      return { ...prev, campaignTableBids: nextTableBudgets };
    });
  }, []);

  const addSlot = () => {
    setForm((prev) => ({
      ...prev,
      slots: [...prev.slots, createDefaultSlot()],
    }));
  };

  const removeSlot = (localId) => {
    setForm((prev) => {
      if (prev.slots.length <= 1) return prev;
      return { ...prev, slots: prev.slots.filter((s) => s.localId !== localId) };
    });
  };

  const toggleDay = (dayIndex) => {
    setForm((prev) => {
      const days = [...prev.selectedDays];
      const i = days.indexOf(dayIndex);
      if (i >= 0) days.splice(i, 1);
      else days.push(dayIndex);
      days.sort((a, b) => a - b);
      return { ...prev, selectedDays: days };
    });
  };

  const updateSlotCase = (slotLocalId, caseType, caseId, patch) => {
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.localId !== slotLocalId) return s;
        return {
          ...s,
          cases: {
            ...s.cases,
            [caseType]: (s.cases?.[caseType] ?? []).map((c) =>
              c.localId === caseId ? { ...c, ...patch } : c
            ),
          },
        };
      }),
    }));
  };

  const addSlotCase = (slotLocalId, caseType) => {
    const newCase = createDefaultCase(caseType);
    let added = false;
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.localId !== slotLocalId) return s;
        const list = s.cases?.[caseType] ?? [];
        const max = caseType === CASE_TYPES.PAUSE ? 1 : 2;
        if (list.length >= max) return s;
        added = true;
        return {
          ...s,
          cases: {
            ...s.cases,
            [caseType]: [...list, newCase],
          },
        };
      }),
    }));
    return added ? newCase.localId : null;
  };

  const removeSlotCase = (slotLocalId, caseType, caseId) => {
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => {
        if (s.localId !== slotLocalId) return s;
        return {
          ...s,
          cases: {
            ...s.cases,
            [caseType]: (s.cases?.[caseType] ?? []).filter((c) => c.localId !== caseId),
          },
        };
      }),
    }));
  };

  const handleCaseFilterOutput = (slotLocalId, caseLocalId) => (output) => {
    setForm((prev) => {
      const platformIds = prev.selectedCampaignIds
        .map((mid) => prev.campaignMeta[mid]?.platformId)
        .filter(Boolean);
      return {
        ...prev,
        slots: prev.slots.map((s) => {
          if (s.localId !== slotLocalId) return s;
          const nextCases = { ...s.cases };
          Object.keys(nextCases).forEach((caseType) => {
            nextCases[caseType] = nextCases[caseType].map((c) => {
              if (c.localId !== caseLocalId) return c;
              const timePeriod = c.perfFilter?.timePeriod ?? "LAST_7_DAY";
              const cubeFilters = buildCubeFiltersFromFilterModule(output, {
                timePeriod,
                platformCampaignIds: platformIds,
                userPlatform,
              });
              const hasFilter =
                filterModuleHasContent(output) || Boolean(cubeFilters?.trim());
              return {
                ...c,
                perfFilter: {
                  ...c.perfFilter,
                  enabled: hasFilter,
                  filterModuleFilters: output,
                  cubeFilters: cubeFilters || "",
                },
              };
            });
          });
          return { ...s, cases: nextCases };
        }),
      };
    });
  };

  const handleCaseTimePeriodChange = (slotLocalId, caseLocalId, timePeriod) => {
    setForm((prev) => {
      const platformIds = prev.selectedCampaignIds
        .map((mid) => prev.campaignMeta[mid]?.platformId)
        .filter(Boolean);
      return {
        ...prev,
        slots: prev.slots.map((s) => {
          if (s.localId !== slotLocalId) return s;
          const nextCases = { ...s.cases };
          Object.keys(nextCases).forEach((caseType) => {
            nextCases[caseType] = nextCases[caseType].map((c) => {
              if (c.localId !== caseLocalId) return c;
              const customDate = timePeriodToCustomDate(timePeriod);
              const nextPerfFilter = {
                ...c.perfFilter,
                timePeriod,
                customDate,
              };
              if (filterModuleHasContent(c.perfFilter?.filterModuleFilters)) {
                const cubeFilters = buildCubeFiltersFromFilterModule(
                  c.perfFilter.filterModuleFilters,
                  {
                    timePeriod,
                    platformCampaignIds: platformIds,
                    userPlatform,
                  }
                );
                nextPerfFilter.cubeFilters = cubeFilters || "";
              }
              return { ...c, perfFilter: nextPerfFilter };
            });
          });
          return { ...s, cases: nextCases };
        }),
      };
    });
  };

  const onSave = async () => {
    const err = validateForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    if (!clientId) {
      toast.error("Select a client first");
      return;
    }
    setIsSaving(true);
    try {
      const body = formToApiRequestBody(form, {
        clientId,
        email,
        ruleId: editId,
        createdAt,
        userPlatform,
      });
      if (editId) {
        const { data: res } = await axios.patch(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set/${editId}?source=${userPlatform}&type=${RULE_TYPE}`,
          body
        );
        if (res?.success === false && !res?.id) {
          throw new Error("Failed to update rule");
        }
        toast.success("Bid Automation rule updated successfully");
      } else {
        const { data } = await axios.post(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set?source=${userPlatform}&type=${RULE_TYPE}`,
          body
        );
        const res = data?.data ?? data;
        if (!res?.id && res?.success !== true) {
          throw new Error("Failed to create rule");
        }
        toast.success("Bid Automation rule created successfully");
      }
      navigate("/bid-automation-list");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Failed to save rule"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const dayHelperText =
    form.selectedDays.length === 7
      ? "All Days"
      : form.selectedDays.length === 0
        ? "None"
        : form.selectedDays.map((i) => formatDayDisplayName(i)).join(", ");

  if (isEditHydrating) {
    return (
      <div
        className={
          dashboardSideBarVisibiltyState
            ? "main-content-container-onopen-mode"
            : "main-content-container-oncolapse-mode"
        }
        id="main-box"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            width: "100%",
            minHeight: "calc(100vh - 86px)",
          }}
        >
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box
        sx={{
          py: 1,
          px: { xs: 2, md: 3 },
          pb: 5,
          minHeight: "100%",
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 3.5 }}>
          <IconButton
            size="small"
            aria-label="Back to Bid Automation list"
            onClick={() => navigate("/bid-automation-list")}
            sx={{
              mt: 0.25,
              ml: -0.5,
              bgcolor: "#fff",
              border: `1px solid ${BID_V2_COLORS.border}`,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <ArrowBackIcon fontSize="small" sx={{ color: BID_V2_COLORS.title }} />
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: HEADING_TITLE_COLOR, fontSize: "1.375rem", mb: 0.5 }}
            >
              {editId ? "Edit Bid Automation Rule" : "Create Bid Automation Rule"}
            </Typography>
            <Typography variant="body2" sx={{ ...sectionSubtitleSx }}>
              Configure targeting bids by time slots, dayparting, and performance filters.
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={3}>
          {/* Basic Details */}
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
              <BidSectionHeader
                step={1}
                title="Basic Details"
                description="Give your automation rule a unique name"
              />
              <TextField
                fullWidth
                id="rule-name"
                label="Rule Name"
                placeholder="Enter rule name"
                value={form.ruleName}
                onChange={(e) => {
                  enableValidation();
                  patchForm({ ruleName: e.target.value });
                }}
                disabled={editId}
                sx={textFieldSx}
                error={!nameValidation.loading && !nameValidation.valid && Boolean(nameValidation.message)}
              />
              {nameValidation.loading && (
                <Typography
                  sx={{ mt: 1.25, fontSize: "0.8125rem", fontWeight: 500, color: BID_V2_COLORS.muted }}
                >
                  Validating rule name…
                </Typography>
              )}
              {!nameValidation.loading && !nameValidation.valid && nameValidation.message && (
                <Typography
                  role="alert"
                  sx={{
                    mt: 1.25,
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "error.main",
                    lineHeight: 1.5,
                  }}
                >
                  {nameValidation.message}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Campaigns */}
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
              <BidSectionHeader
                step={2}
                title="Selected Campaigns & Bid Caps"
                description="Choose campaigns and set daily, min, and max budget limits"
              />
              <Stack spacing={2.5}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={isLoadingPortfolios}
                  options={portfolioOptions}
                  value={selectedPortfolios}
                  onChange={(_, value) => setSelectedPortfolios(value)}
                  getOptionLabel={getPortfolioDisplayName}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?.id ?? getPortfolioPlatformId(option)}
                        label={getPortfolioDisplayName(option)}
                        size="small"
                      />
                    ))
                  }
                  sx={{
                    ...textFieldSx,
                    maxWidth: "100%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Portfolio (optional)"
                      placeholder={
                        selectedPortfolios.length
                          ? `${selectedPortfolios.length} portfolio(s) selected`
                          : "Filter campaigns by portfolio"
                      }
                      sx={textFieldSx}
                    />
                  )}
                />
                <Typography sx={{ ...sectionSubtitleSx, mt: -1 }}>
                  Leave portfolio empty to browse all campaigns, or select one or more portfolios
                  to narrow the campaign list.
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={isLoadingCampaigns}
                  options={isLoadingCampaigns ? [] : campaignAutocompleteOptions}
                  noOptionsText={
                    isLoadingCampaigns ? "" : "No campaigns found"
                  }
                  value={selectedCampaignObjects}
                  inputValue={campaignSearchInput}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setCampaignSearchInput(value);
                    }
                  }}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) =>
                    option?.__selectAll ? CAMPAIGN_SELECT_ALL_LABEL : getCampaignDisplayName(option)
                  }
                  isOptionEqualToValue={(a, b) => {
                    if (a?.__selectAll && b?.__selectAll) return true;
                    return getCampaignMongoId(a) === getCampaignMongoId(b);
                  }}
                  onChange={handleCampaignAutocompleteChange}
                  renderTags={() => null}
                  sx={{
                    ...textFieldSx,
                    maxWidth: "100%",
                    "& .MuiAutocomplete-listbox": {
                      maxHeight: 280,
                      py: 0.5,
                    },
                    "& .MuiAutocomplete-option": {
                      display: "flex",
                      alignItems: "flex-start",
                      py: 1.25,
                      px: 2,
                    },
                  }}
                  slots={{ paper: BidCampaignSelectPaper }}
                  slotProps={{
                    paper: {
                      activeFilter: form.activeCampaignFilter,
                      onFilterChange: (f) => patchForm({ activeCampaignFilter: f }),
                      isLoading: isLoadingCampaigns,
                    },
                    popper: {
                      placement: "bottom-start",
                      modifiers: [{ name: "flip", enabled: false }],
                    },
                  }}
                  renderOption={(props, option, { selected }) => {
                    if (option?.__selectAll) {
                      return (
                        <li {...props} key={CAMPAIGN_SELECT_ALL_ID}>
                          <Checkbox
                            size="small"
                            checked={allVisibleCampaignsSelected}
                            indeterminate={
                              someVisibleCampaignsSelected && !allVisibleCampaignsSelected
                            }
                            sx={{ mr: 1, flexShrink: 0 }}
                          />
                          <Typography
                            component="span"
                            sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3125 }}
                          >
                            {CAMPAIGN_SELECT_ALL_LABEL}
                          </Typography>
                        </li>
                      );
                    }
                    return (
                    <li {...props} key={getCampaignMongoId(option)}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1, mt: 0.25, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minHeight: "1.3125rem",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: getCampaignStatusDotColor(option),
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              lineHeight: 1.3125,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getCampaignDisplayName(option)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: BID_V2_COLORS.muted,
                            pl: 2,
                            mt: 0.25,
                          }}
                        >
                          {`Campaign ID: ${getCampaignPlatformId(option) || "-"}`}
                        </Typography>
                      </Box>
                    </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Campaigns"
                      placeholder={
                        form.selectedCampaignIds.length
                          ? `${form.selectedCampaignIds.length} campaign(s) selected`
                          : "Select campaigns"
                      }
                      sx={textFieldSx}
                    />
                  )}
                />
                {form.selectedCampaignIds.length === 0 ? (
                  <Alert severity="info" sx={infoAlertSx}>
                    Select campaigns to configure bid caps and automation slots.
                  </Alert>
                ) : (
                  <>
                  {form.selectedCampaignIds.length > CAMPAIGN_TABLE_PAGE_SIZE && (
                    <Alert severity="info" sx={infoAlertSx}>
                      Showing {CAMPAIGN_TABLE_PAGE_SIZE} campaigns per page. Use pagination below
                      the table to edit all {form.selectedCampaignIds.length} selected campaigns.
                    </Alert>
                  )}
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 320, borderRadius: 2, borderColor: BID_V2_COLORS.border }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Campaign", "Status", "Lower Cap", "Upper Cap", ""].map((h) => (
                            <TableCell
                              key={h}
                              align={tableHeaderAlign(h)}
                              sx={{
                                ...tableHeadCellSx,
                                ...(h === "Campaign" && {
                                  width: 300,
                                  maxWidth: 300,
                                }),
                              }}
                            >
                              {h === "Lower Cap" ? (
                                <BidBulkColumnHeader
                                  label="Lower Cap"
                                  infoTooltip={`Bid automation won't reduce targeting bids below this amount. SP bid range for this marketplace: ${formatSpBidBound(getMinCampaignBudget())}–${formatSpBidBound(getMaxCampaignBid())} ${getCampaignBudgetCurrencyCode()}.`}
                                  columnType={BULK_UPDATE_COLUMN.MIN_BID}
                                  onApply={applyBulkCampaignMinBid}
                                  disabled={form.selectedCampaignIds.length === 0}
                                />
                              ) : h === "Upper Cap" ? (
                                <BidBulkColumnHeader
                                  label="Upper Cap"
                                  infoTooltip={`Bid automation won't increase targeting bids beyond this amount. SP bid range for this marketplace: ${formatSpBidBound(getMinCampaignBudget())}–${formatSpBidBound(getMaxCampaignBid())} ${getCampaignBudgetCurrencyCode()}.`}
                                  columnType={BULK_UPDATE_COLUMN.MAX_BID}
                                  onApply={applyBulkCampaignMaxBid}
                                  disabled={form.selectedCampaignIds.length === 0}
                                />
                              ) : (
                                h
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedCampaignIds.map((mid) => {
                          const meta = form.campaignMeta[mid] ?? {};
                          const row = form.campaignTableBids[mid] ?? {};
                          const minBudgetError = getMinBudgetError(row.min_value);
                          const maxBudgetError = getMaxBudgetError(row.max_value);
                          const statusRaw = String(
                            meta.status ?? row.status ?? "enabled"
                          ).toLowerCase();
                          const statusLabel =
                            statusRaw === "enabled" || statusRaw === "active"
                              ? "Enabled"
                              : statusRaw === "paused" || statusRaw === "disabled"
                              ? "Paused"
                              : "Unknown";
                          const statusChipSx =
                            statusLabel === "Enabled"
                              ? {
                                  bgcolor: "rgba(16, 185, 129, 0.12)",
                                  color: "#047857",
                                }
                              : statusLabel === "Paused"
                              ? {
                                  bgcolor: "rgba(245, 158, 11, 0.14)",
                                  color: "#B45309",
                                }
                              : {
                                  bgcolor: "rgba(100, 116, 139, 0.12)",
                                  color: "#334155",
                                };
                          return (
                            <TableRow key={mid} hover sx={{verticalAlign:"baseline"}}>
                              <TableCell align={tableBodyAlign("Campaign")} sx={tableBodyCellSx}>
                                <CampaignTableNameCell
                                  name={meta.name}
                                  campaignId={mid}
                                  loading={isCampaignNameLoading(mid)}
                                />
                              </TableCell>
                              <TableCell align={tableBodyAlign("Status")} sx={tableBodyCellSx}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <Chip
                                    label={statusLabel}
                                    size="small"
                                    sx={{
                                      minWidth: 88,
                                      fontSize: "0.75rem",
                                      fontWeight: 700,
                                      ...statusChipSx,
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align={tableBodyAlign("Lower Cap")} sx={tableBodyCellSx}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={row.min_value ?? ""}
                                    onChange={(e) =>
                                      patchForm({
                                        campaignTableBids: {
                                          ...form.campaignTableBids,
                                          [mid]: {
                                            ...row,
                                            min_value: normalizeMinBudgetInput(e.target.value),
                                          },
                                        },
                                      })
                                    }
                                    error={Boolean(minBudgetError)}
                                    helperText={minBudgetError}
                                    inputProps={numberFieldInputProps}
                                    FormHelperTextProps={{
                                      sx: { mx: 0, fontSize: "0.6875rem", lineHeight: 1.2 },
                                    }}
                                    sx={{ ...textFieldSx, width: 108 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align={tableBodyAlign("Upper Cap")} sx={tableBodyCellSx}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={row.max_value ?? ""}
                                    onChange={(e) =>
                                      patchForm({
                                        campaignTableBids: {
                                          ...form.campaignTableBids,
                                          [mid]: {
                                            ...row,
                                            max_value: normalizeMaxBudgetInput(e.target.value),
                                          },
                                        },
                                      })
                                    }
                                    error={Boolean(maxBudgetError)}
                                    helperText={maxBudgetError}
                                    inputProps={numberFieldInputProps}
                                    FormHelperTextProps={{
                                      sx: { mx: 0, fontSize: "0.6875rem", lineHeight: 1.2 },
                                    }}
                                    sx={{ ...textFieldSx, width: 108 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align={tableBodyAlign("")} sx={tableBodyCellSx}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Remove ${meta.name}`}
                                  onClick={() => {
                                    const c = campaignOptions.find((o) => getCampaignMongoId(o) === mid);
                                    toggleCampaign(mid, c || { id: mid });
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <BidTablePagination
                    count={form.selectedCampaignIds.length}
                    page={campaignBudgetPage}
                    onPageChange={setCampaignBudgetPage}
                  />
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Targeting Exclusion */}
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
              <BidSectionHeader
                step={3}
                title="Set Targeting Exclusion"
                description="Exclude Automatic and/or Broad keyword targetings from bid changes"
                tooltip={TARGETING_EXCLUSION_TOOLTIP}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.targetingExclusion?.enabled)}
                    onChange={(e) =>
                      patchForm({
                        targetingExclusion: {
                          ...form.targetingExclusion,
                          enabled: e.target.checked,
                          automatic: e.target.checked ? form.targetingExclusion?.automatic : false,
                          broad: e.target.checked ? form.targetingExclusion?.broad : false,
                        },
                      })
                    }
                  />
                }
                label="Enable targeting exclusion"
                sx={radioLabelSx}
              />
              {form.targetingExclusion?.enabled ? (
                <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(form.targetingExclusion?.automatic)}
                        onChange={(e) =>
                          patchForm({
                            targetingExclusion: {
                              ...form.targetingExclusion,
                              automatic: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Automatic"
                    sx={radioLabelSx}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(form.targetingExclusion?.broad)}
                        onChange={(e) =>
                          patchForm({
                            targetingExclusion: {
                              ...form.targetingExclusion,
                              broad: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Broad Keyword"
                    sx={radioLabelSx}
                  />
                </Stack>
              ) : null}
            </CardContent>
          </Card>

          {/* Bid Automation */}
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
              <BidSectionHeader
                step={4}
                title="Bid Automation"
                description="Set schedule, automation mode, and per-slot bid changes"
              />
              <RadioGroup
                row
                value={form.automationMode}
                onChange={(e) => handleAutomationModeSelect(e.target.value)}
                sx={{ gap: 2, mb: 2.5 }}
              >
                {AUTOMATION_MODE_OPTIONS.map((o) => (
                  <FormControlLabel
                    key={o.value}
                    value={o.value}
                    control={<Radio size="small" />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                        <span>{o.label}</span>
                        {o.tooltip ? <BidInfoTooltip title={o.tooltip} /> : null}
                      </Stack>
                    }
                    sx={radioLabelSx}
                  />
                ))}
              </RadioGroup>

              <Box sx={nestedScheduleBoxSx}>
                <Typography sx={{ ...fieldLabelSx, mb: 1.5 }}>Select Rule Duration</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start date"
                      InputLabelProps={{ shrink: true }}
                      inputRef={scheduleStartRef}
                      inputProps={{ min: todayISO }}
                      value={form.scheduleStartDate}
                      onChange={(e) => {
                        enableValidation();
                        patchForm({ scheduleStartDate: e.target.value });
                      }}
                      onClick={() => scheduleStartRef.current?.showPicker?.()}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End date"
                      InputLabelProps={{ shrink: true }}
                      inputRef={scheduleEndRef}
                      inputProps={{
                        min: form.scheduleStartDate || todayISO,
                      }}
                      value={form.scheduleEndDate}
                      onChange={(e) => {
                        enableValidation();
                        patchForm({ scheduleEndDate: e.target.value });
                      }}
                      onClick={() => scheduleEndRef.current?.showPicker?.()}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>

                {scheduleValidation.loading && (
                  <Typography
                    sx={{ mt: 1.25, fontSize: "0.8125rem", fontWeight: 500, color: BID_V2_COLORS.muted }}
                  >
                    Validating schedule…
                  </Typography>
                )}
                {!scheduleValidation.loading && scheduleValidation.issues.length > 0 && (
                  <Box sx={{ mt: 1.25 }} role="alert">
                    {scheduleValidation.issues.map((issue, index) => (
                      <Typography
                        key={`${issue.issue_type ?? "issue"}-${index}`}
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          color: "error.main",
                          lineHeight: 1.5,
                          mb: index < scheduleValidation.issues.length - 1 ? 0.5 : 0,
                        }}
                      >
                        {formatValidationIssueMessage(issue?.message)}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ ...fieldLabelSx, mb: 1.5 }}>Select Days</Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {DAY_LABELS.map((label, i) => (
                        <Button
                          key={`${label}-${i}`}
                          variant={form.selectedDays.includes(i) ? "contained" : "outlined"}
                          onClick={() => toggleDay(i)}
                          sx={dayCircleSx(form.selectedDays.includes(i))}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                    <Typography sx={{ mt: 1.25, fontSize: "0.8125rem", fontWeight: 500, color: BID_V2_COLORS.muted }}>
                      Selected: {dayHelperText}
                    </Typography>
                  </Box>

              </Box>

              {form.slots.map((slot, index) => {
                const hasDurationError = Boolean(slotDurationErrors[slot.localId]);
                return (
                  <Paper
                    key={slot.localId}
                    variant="outlined"
                    sx={{
                      ...slotPaperSx,
                      ...(hasDurationError && { borderColor: "error.main" }),
                    }}
                  >
                    <Typography sx={{ ...durationLabelSx, mb: 1.5 }}>SLOT DURATION {index + 1}</Typography>

                    <Box sx={durationRowLayoutSx}>
                      <Box sx={durationFieldsGridSx}>
                      {form.automationMode === "DAYPARTING" ? (
                        <>
                          <FormControl fullWidth sx={selectFieldSx}>
                            <InputLabel shrink>Start Time</InputLabel>
                            <Select
                              label="Start Time"
                              notched
                              displayEmpty
                              value={slot.startTime || ""}
                              error={Boolean(slotDurationErrors[slot.localId])}
                              onChange={(e) => updateSlot(slot.localId, { startTime: e.target.value })}
                              renderValue={(selected) =>
                                selected || (
                                  <Box component="span" sx={{ color: BID_V2_COLORS.muted }}>
                                    Select start time
                                  </Box>
                                )
                              }
                            >
                              {SLOT_START_HOURS.map((h) => (
                                <MenuItem key={h.value} value={h.value}>
                                  {h.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth sx={selectFieldSx}>
                            <InputLabel shrink>End Time</InputLabel>
                            <Select
                              label="End Time"
                              notched
                              displayEmpty
                              value={slot.endTime || ""}
                              error={Boolean(slotDurationErrors[slot.localId])}
                              onChange={(e) => updateSlot(slot.localId, { endTime: e.target.value })}
                              renderValue={(selected) =>
                                selected || (
                                  <Box component="span" sx={{ color: BID_V2_COLORS.muted }}>
                                    Select end time
                                  </Box>
                                )
                              }
                            >
                              {SLOT_END_HOURS.map((h) => (
                                <MenuItem key={h.value} value={h.value}>
                                  {h.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            type="date"
                            label="Start Date"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: ruleScheduleDateBounds.min,
                              max: slot.endDate || ruleScheduleDateBounds.max,
                            }}
                            value={slot.startDate}
                            onChange={(e) => updateSlot(slot.localId, { startDate: e.target.value })}
                            onClick={openDatePickerFromField}
                            error={Boolean(slotDurationErrors[slot.localId])}
                            sx={{ ...textFieldSx, cursor: "pointer", "& input": { cursor: "pointer" } }}
                          />
                          <TextField
                            fullWidth
                            type="date"
                            label="End Date"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: slot.startDate || ruleScheduleDateBounds.min,
                              max: ruleScheduleDateBounds.max,
                            }}
                            value={slot.endDate}
                            onChange={(e) => updateSlot(slot.localId, { endDate: e.target.value })}
                            onClick={openDatePickerFromField}
                            error={Boolean(slotDurationErrors[slot.localId])}
                            sx={{ ...textFieldSx, cursor: "pointer", "& input": { cursor: "pointer" } }}
                          />
                        </>
                      )}
                      <FormControl fullWidth sx={selectFieldSx}>
                        <InputLabel shrink>At min/max limits</InputLabel>
                        <Select
                          label="At min/max limits"
                          notched
                          value={slot.status}
                          onChange={(e) => updateSlot(slot.localId, { status: e.target.value })}
                        >
                          {SLOT_STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.menuLabel}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      </Box>
                      <Box sx={durationRowActionsSx}>
                        <IconButton
                          aria-label="Remove schedule"
                          disabled={form.slots.length <= 1}
                          onClick={() => removeSlot(slot.localId)}
                          sx={{
                            ...slotActionIconSx({ disabled: form.slots.length <= 1 }),
                            ...(form.slots.length > 1 && { color: "error.main" }),
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {slotDurationErrors[slot.localId] ? (
                      <Typography
                        role="alert"
                        sx={{
                          mt: 1,
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          color: "error.main",
                          lineHeight: 1.5,
                        }}
                      >
                        {slotDurationErrors[slot.localId]}
                      </Typography>
                    ) : null}

                    <BidSlotCasesPanel
                      slot={slot}
                      expandedPerfCaseId={expandedPerfCaseId}
                      setExpandedPerfCaseId={setExpandedPerfCaseId}
                      onUpdateSlot={(patch) => updateSlot(slot.localId, patch)}
                      onUpdateCase={(caseType, caseId, patch) =>
                        updateSlotCase(slot.localId, caseType, caseId, patch)
                      }
                      onAddCase={(caseType) => addSlotCase(slot.localId, caseType)}
                      onRemoveCase={(caseType, caseId) =>
                        removeSlotCase(slot.localId, caseType, caseId)
                      }
                      onCaseFilterOutput={(caseLocalId) =>
                        handleCaseFilterOutput(slot.localId, caseLocalId)
                      }
                      onCaseTimePeriodChange={(caseLocalId, tp) =>
                        handleCaseTimePeriodChange(slot.localId, caseLocalId, tp)
                      }
                      nonNegativeValue={nonNegativeValue}
                    />
                  </Paper>
                );
              })}

              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={addSlot}
                sx={{
                  mt: 1,
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: BID_V2_COLORS.title,
                  borderColor: BID_V2_COLORS.border,
                  borderRadius: 2,
                  textTransform: "none",
                  "&:hover": { borderColor: "primary.main", bgcolor: "primary.50", color: "primary.main" },
                }}
              >
                Add New Schedule
              </Button>
            </CardContent>
          </Card>

          {/* Gap Handling */}
          <Card variant="outlined" sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
              <BidSectionHeader
                step={5}
                title="Gap Handling"
                description="Define behavior for time outside scheduled slots"
                tooltip={GAP_HANDLING_SECTION_TOOLTIP}
              />
              <RadioGroup
                row
                value={form.gapHandling}
                onChange={(e) => patchForm({ gapHandling: e.target.value })}
                sx={{ gap: { xs: 0.5, sm: 2 }, flexWrap: "wrap" }}
              >
                {GAP_OPTIONS.map((o) => (
                  <FormControlLabel
                    key={o.value}
                    value={o.value}
                    control={<Radio size="small" />}
                    label={o.label}
                    sx={radioLabelSx}
                  />
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/bid-automation-list")}
              disabled={isSaving}
              sx={{
                px: 3,
                py: 1.1,
                fontWeight: 600,
                textTransform: "none",
                borderColor: BID_V2_COLORS.border,
                color: BID_V2_COLORS.body,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={isSaving}
              sx={{ px: 3, py: 1.1, fontWeight: 600, textTransform: "none", boxShadow: "none" }}
            >
              {isSaving ? <CircularProgress size={22} color="inherit" /> : editId ? "Update Rule" : "Publish Rule"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={automationModeConfirmOpen}
        onClose={handleCancelAutomationModeSwitch}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: BID_V2_COLORS.title }}>
          Switch to {pendingAutomationModeLabel}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: BID_V2_COLORS.body, lineHeight: 1.6 }}>
            All details below this section will be reset, including selected days, slot durations,
            per-slot bid settings, and performance filter configuration.
          </Typography>
          <Typography
            sx={{ fontSize: "0.875rem", color: "warning.main", fontWeight: 600, mt: 1.5, lineHeight: 1.6 }}
          >
            This cannot be undone. Your campaign selection and rule start/end dates will be kept.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={handleCancelAutomationModeSwitch}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmAutomationModeSwitch}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            Switch & reset
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearAllCampaignsConfirmOpen}
        onClose={() => setClearAllCampaignsConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: BID_V2_COLORS.title }}>
          Clear all selected campaigns?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: BID_V2_COLORS.body, lineHeight: 1.6 }}>
            This will remove all{" "}
            <strong>{form.selectedCampaignIds.length}</strong> selected campaign
            {form.selectedCampaignIds.length === 1 ? "" : "s"} from this rule. To remove a single
            campaign, use the delete icon in the table below.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={() => setClearAllCampaignsConfirmOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmClearAllCampaigns}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            Clear all campaigns
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default BidAutomationCreation;
